<template>
  <div class="container bg-white shadow-lg rounded-sm border border-slate-200 relative mx-auto">
    <div class="p-5 text-lg bg-[#F8FAFC] font-semibold">Cost breakdown</div>

    <div class="p-5">
      <!-- Totals -->
      <div class="border-b border-slate-200 pb-4 mb-4">
        <div class="flex justify-between items-center">
          <h3 class="text-lg font-semibold text-slate-800">Monthly total</h3>
          <span
            :key="totalMonthlyCost"
            v-currency="totalMonthlyCost"
            class="text-xl font-bold text-slate-800"
          >
            {{ totalMonthlyCost }}
          </span>
        </div>
        <div class="flex justify-between items-center mt-2">
          <span class="text-sm text-slate-600">12-month estimate</span>
          <span
            :key="totalYearlyCost"
            v-currency="totalYearlyCost"
            class="text-sm font-medium text-slate-600"
          >
            {{ totalYearlyCost }}
          </span>
        </div>
      </div>

      <!-- Per category -->
      <div v-if="categories.length" class="mb-6">
        <h4 class="text-md font-semibold text-slate-800 mb-3">By service</h4>
        <div class="space-y-2">
          <div v-for="category in categories" :key="category.id">
            <div class="flex justify-between items-center text-sm mb-1">
              <span class="text-slate-700">{{ category.name }}</span>
              <span v-currency="category.monthlyCost" class="font-medium text-slate-700">
                {{ category.monthlyCost }}
              </span>
            </div>
            <div class="h-2 bg-slate-100 rounded overflow-hidden">
              <div
                class="h-full bg-indigo-500 rounded"
                :style="{ width: `${category.share}%` }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Per line -->
      <div v-if="services.length > 0" class="space-y-4">
        <h4 class="text-md font-semibold text-slate-800 mb-3">Service breakdown</h4>

        <div
          v-for="service in services"
          :key="service.id"
          class="border border-slate-200 rounded-lg overflow-hidden"
        >
          <div class="flex justify-between items-start gap-4 p-4">
            <div class="min-w-0">
              <h5 class="font-semibold text-slate-800">{{ service.name }}</h5>
              <p class="text-sm text-slate-600 mt-1">{{ service.description }}</p>
              <p v-if="service.quantity > 1" class="text-xs text-slate-500 mt-1">
                {{ service.quantity }} ×
                <span v-currency="service.unitMonthlyCost">{{ service.unitMonthlyCost }}</span>
                per month
              </p>
            </div>
            <div class="text-right shrink-0">
              <div v-if="service.onRequest" class="font-semibold text-indigo-600 text-sm">
                On request
              </div>
              <div v-else v-currency="service.monthlyCost" class="font-semibold text-slate-800">
                {{ service.monthlyCost }}
              </div>
              <div class="text-xs text-slate-500">per month</div>
            </div>
          </div>

          <LineItems v-if="service.items && service.items.length" :items="service.items" />
        </div>
      </div>

      <!-- Notice -->
      <div class="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded">
        <div class="flex items-start">
          <div class="text-yellow-600 mr-2">⚠️</div>
          <div class="text-sm text-yellow-800">
            <strong>Pricing information:</strong>
            <ul class="mt-1 space-y-1">
              <li>• Prices shown are estimates and may vary based on actual usage</li>
              <li>• All prices are in {{ currentCurrency }} and exclude applicable taxes</li>
              <li>• Items marked "on request" are quoted individually and are not included in the total</li>
              <li>• Support plans are recalculated whenever the rest of the estimate changes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import LineItems from "@/components/config/LineItems.vue";
import { services as catalog } from "@/utils/services";
import { round } from "@/utils/pricing";

export default {
  name: "CostBreakdown",
  components: { LineItems },
  props: {
    services: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters({
      currentCurrency: "services/currentCurrency",
    }),
    totalMonthlyCost() {
      return this.services.reduce((total, item) => total + (item.monthlyCost ?? 0), 0);
    },
    totalYearlyCost() {
      return this.services.reduce((total, item) => total + (item.totalCost ?? 0), 0);
    },
    categories() {
      const totals = new Map();

      this.services.forEach((service) => {
        const id = service.serviceId || "other";
        totals.set(id, (totals.get(id) || 0) + (service.monthlyCost || 0));
      });

      const max = Math.max(...totals.values(), 0);

      return [...totals.entries()]
        .filter(([, monthlyCost]) => monthlyCost > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([id, monthlyCost]) => ({
          id,
          name: catalog.find((entry) => entry.id === id)?.name || "Other",
          monthlyCost: round(monthlyCost),
          share: max > 0 ? (monthlyCost / max) * 100 : 0,
        }));
    },
  },
};
</script>
